import { PageHeader, Text1 } from '@touche-lts/panel-ui/dist'
import { IconBin, IconProjects } from 'assets'
import { Library, Monitor, TimeLine } from 'components'
import { getProductsFromSession } from 'components/editor/Monitor/VideoArea/InteractiveAreaCanvas/utils'
import { inject } from 'mobx-react'
import { autoObjectDetectionTypes } from 'models'
import { PageContainer } from 'pages'
import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useHotkeys } from 'react-hotkeys-hook'
import { EDITOR_HOTKEYS_STORE, EDITOR_STORE, GENERAL_STORE, Hotkey as HotkeyProps, NOTIFICATIONS_STORE, PROJECT_STORE } from 'store'
import styles from './EditorPage.module.scss'
import { EditorPageProps } from './types'
import { formatNumberShort } from '../../../utilities'
import { isEmpty } from 'validate.js'

const Hotkey = ({ keyCombination, action }: HotkeyProps) => {
  const preventDefaultAndTriggerAction = e => {
    e.preventDefault()
    action()
  }
  useHotkeys(keyCombination, preventDefaultAndTriggerAction)
  return null
}

// Status message mapping
const getStatusMessage = (status: autoObjectDetectionTypes): string => {
  const messages = {
    [autoObjectDetectionTypes.NOT_STARTED]: 'Initializing the engines…',
    [autoObjectDetectionTypes.PROCESSING]: 'Video processing started…',
    [autoObjectDetectionTypes.FRAME_STORE_START]: 'Reviewing video frame by frame.',
    [autoObjectDetectionTypes.FRAME_STORE_COMPLETE]: 'Video review completed.',
    [autoObjectDetectionTypes.GEMINI_START]: 'AI processing initialized.',
    [autoObjectDetectionTypes.GEMINI_COMPLETE]: 'AI processing completed.',
    [autoObjectDetectionTypes.OPENCV_START]: 'Other frame detection and coordinate descaling started.',
    [autoObjectDetectionTypes.OPENCV_COMPLETE]: 'Other frame detection and coordinate descaling completed.',
    [autoObjectDetectionTypes.COMPLETED]: 'Finalized.'
  }
  return messages[status] || ''
}

// Define processing states (not yet completed)
const isProcessingState = (status: autoObjectDetectionTypes): boolean => {
  return status !== autoObjectDetectionTypes.COMPLETED && status !== autoObjectDetectionTypes.NOT_STARTED
}

const EditorPage : React.FC<EditorPageProps> = (props) => {
  const {
    [EDITOR_STORE]: {
      openForEdit, clearEditorState, generateBox, recycleInteractiveArea, attachSuggestProducts,
      project, getProjectDetailsById, updateZIndex
    },
    [EDITOR_HOTKEYS_STORE]: { hotkeys },
    match: { params: { id } },
    history: { push },
    [PROJECT_STORE]: { getProjectById, projectToView },
    [GENERAL_STORE]: { showSuccess, setLoadingOff, showError, setLoadingOn },
    [NOTIFICATIONS_STORE]: { getNotifications }
  } = props

  useEffect(() => {
    clearEditorState()
    openForEdit(id)
    getProjectById(id)
  }, [openForEdit, id])

  const [showButtons, setShowButtons] = useState(false)
  const [disableState, setDisableState] =
  useState({
    autoObjectDetectionState: false,
    smartSuggestionState: true,
    deleteInteractiveAreaState: true
  })
  const [showText, setShowText] = useState('')

  useEffect(() => {
    const syncWithStorage = () => {
      const isReady = localStorage.getItem('isReadyForAutoGenerateBox') === 'true'
      setShowButtons(isReady)
    }

    window.addEventListener('local-storage-change', syncWithStorage)

    return () => {
      window.removeEventListener('local-storage-change', syncWithStorage)
      localStorage.removeItem('isReadyForAutoGenerateBox')
      sessionStorage.removeItem('preloadProducts')
    }
  }, [])

  // Handle autoObjectDetection flags using API polling every 15 sec
  useEffect(() => {
    async function handleApiPoll () {
      if (project.id) {
        const projectData = await getProjectDetailsById(project)
        return projectData
      }
    }

    handleApiPoll()

    const status = project.autoObjectDetection

    if (status === autoObjectDetectionTypes.COMPLETED) {
      setDisableState({
        autoObjectDetectionState: true,
        smartSuggestionState: false,
        deleteInteractiveAreaState: false
      })
      setShowText('')
      return
    } else if (status && isProcessingState(status)) {
      setDisableState({
        autoObjectDetectionState: true,
        smartSuggestionState: true,
        deleteInteractiveAreaState: true
      })
      setShowText(getStatusMessage(status))
    }

    const intervalId = setInterval(async () => {
      setLoadingOff()

      if (project?.id) {
        const projectData = await handleApiPoll()
        getNotifications()
        const currentStatus = projectData.autoObjectDetection

        if (currentStatus === autoObjectDetectionTypes.COMPLETED) {
          clearInterval(intervalId)
          await updateZIndex()
          setShowText(getStatusMessage(autoObjectDetectionTypes.COMPLETED))

          setTimeout(() => {
            setShowText('')
            showSuccess('Auto Object Detection complete.')
          }, 1500)

          setDisableState({
            autoObjectDetectionState: true,
            smartSuggestionState: false,
            deleteInteractiveAreaState: false
          })
        } else if (currentStatus && isProcessingState(currentStatus)) {
          setDisableState({
            autoObjectDetectionState: true,
            smartSuggestionState: true,
            deleteInteractiveAreaState: true
          })
          setShowText(getStatusMessage(currentStatus))
        } else {
          setDisableState({
            autoObjectDetectionState: false,
            smartSuggestionState: true,
            deleteInteractiveAreaState: true
          })
          setShowText('')
        }
      }
    }, 15000)

    return () => clearInterval(intervalId)
  }, [project, getProjectDetailsById])

  async function autoObjectDetectionOnClick () {
    // check products are available in project
    if (Object.keys(getProductsFromSession()).length === 0) {
      showError('Please add products in project.')
      return
    }

    setDisableState({
      autoObjectDetectionState: true,
      deleteInteractiveAreaState: true,
      smartSuggestionState: true
    })
    setShowText(getStatusMessage(autoObjectDetectionTypes.NOT_STARTED))

    // await generateInteractiveArea() // Comment this line - process moved to backend
    await generateBox()

    // updateAutoObjectDetection({ id: +id, autoObjectDetection: true }) // Comment this line - this is handle by backend
  }

  async function smartSuggestionsOnClick () {
    // loading start
    setLoadingOn()

    // attach suggest products
    await attachSuggestProducts()

    // loading off
    setLoadingOff()

    // success message
    showSuccess('Smart Suggestions complete.')
  }

  async function deleteInteractiveAreaOnClick () {
    const deleteInteractiveAreaData = await recycleInteractiveArea()
    if (deleteInteractiveAreaData) {
      openForEdit(id) // refresh project data
      showSuccess('Delete Interactive Area complete.')
      setDisableState({
        autoObjectDetectionState: false,
        smartSuggestionState: true,
        deleteInteractiveAreaState: true
      })
      setLoadingOff()
    }
  }

  return (
    <PageContainer>
      <PageHeader
        className={styles.pageHeader}
        navSegments={[
          {
            label: 'Projects ',
            icon: IconProjects as any,
            onClick: () => push('/projects')
          },
          {
            label: 'Editor'
          }
        ]}

        middleContainerChildren={
          <div className={styles.middleContainer}>
            {showText &&
              <div className={styles.autoBoxStatus}>
                {showText}
              </div>
            }
            {!showButtons && (!isEmpty(project) || !isEmpty(projectToView)) &&
              <div className={styles.tokenUsed}>
                <div className={styles.tokenRow}>
                  <Text1 bold className={styles.tokenLabel}>Input AI Token Used</Text1>
                  <span className={styles.tokenValue}>{formatNumberShort(project?.inputGeminiTokens || projectToView?.inputGeminiTokens || 0)}</span>
                </div>
                <div className={styles.tokenRow}>
                  <Text1 bold className={styles.tokenLabel}>Output AI Token Used</Text1>
                  <span className={styles.tokenValue}>{formatNumberShort(project?.outputGeminiTokens || projectToView?.outputGeminiTokens || 0)}</span>
                </div>
              </div>
            }
          </div>
        }

        {...(showButtons && {
          buttons: [
            {
              preset: 'primary',
              children: 'Auto Object Detection',
              title: 'Auto Object Detection',
              disabled: disableState.autoObjectDetectionState,
              onClick: autoObjectDetectionOnClick
            },
            {
              preset: 'primary',
              children: 'Smart Suggestions',
              disabled: disableState.smartSuggestionState,
              title: 'Smart Suggestions',
              onClick: smartSuggestionsOnClick
            },
            {
              preset: 'primary',
              children: <IconBin fill={styles.white} />,
              style: {
                width: '30%'
              },
              disabled: disableState.deleteInteractiveAreaState,
              title: 'Delete Interactive Area',
              onClick: deleteInteractiveAreaOnClick
            }
          ]
        })}
      />
      {hotkeys().map((hotkey, index) => <Hotkey key={index} {...hotkey} />)}
      <div className={styles.topContainer}>
        <Library />
        <Monitor />
      </div>
      <TimeLine />
    </PageContainer>
  )
}

export default inject(EDITOR_STORE, EDITOR_HOTKEYS_STORE, PROJECT_STORE, GENERAL_STORE, NOTIFICATIONS_STORE)(observer(EditorPage))
